'use client';

import { Icons } from '@/assets/icons';
import MainHeader from '@/shared/components/layout/header/main-header';
import PageContainer from '@/shared/components/layout/page-container';
import { ProfileImage } from '@/shared/components/profile-image';
import { Spacing } from '@/shared/components/spacing';
import { cn } from '@/shared/utils/style/cn';
import { ReactNode, useEffect } from 'react';
import { CustomCarousel } from '@/shared/components/custom-carousel';
import { Img } from '@/shared/components/image';
import { Card } from '@/shared/components/card';
import { USER_APPROVE_STATUS } from '@/domain/user/type/user-approve-status';
import { useUserInfo } from '@/domain/user/hook/use-user-info';
import { LoadingPage } from '@/shared/components/loading-page';
import { useRouterWrapper } from '@/shared/hooks/use-router-wrapper';

export function MainPageClient() {
  const router = useRouterWrapper();
  const { userInfo } = useUserInfo();

  useEffect(() => {
    if (userInfo === null) {
      router.replace('/sign-in');
    }
  }, [router, userInfo]);

  if (userInfo === undefined) {
    return <LoadingPage />;
  }

  if (userInfo === null) {
    return null;
  }

  if (userInfo.status === USER_APPROVE_STATUS.WAITING) {
    return <UnderReview />;
  }

  return (
    <PageContainer className="bg-gray-bg">
      <MainHeader />
      <HomeContent />
    </PageContainer>
  );
}

function UnderReview() {
  return (
    <PageContainer className="bg-gray-bg">
      <UnderReviewHeader />
      <Spacing spacing={16} />
      <div className="px-[16px]">
        <Description
          className="mt-[48px] rounded-[16px] bg-white px-[20px] py-[16px]"
          titleClassName="typo-heading-2"
          title={'Registration under review'}
          detail={'Your application has been submitted. We will notify you by email after review.'}
          icon={
            <div className="text-green100 bg-green10 flex h-[50px] w-[50px] items-center justify-center rounded-full">
              <Icons.Submit width={29.5} height={25} />
            </div>
          }
        />
      </div>
      <Spacing spacing={16} />
      <div className="border-pink120 mx-[16px] flex flex-col gap-[16px] rounded-[16px] border-1 bg-white p-[20px]">
        <div className="typo-heading-2 text-pink120 text-center font-semibold">
          Build Your Glow. Grow Your Voice.
        </div>
        <div className="typo-body-2 text-pink120 text-center font-medium">
          Your story, your growth, <br />
          your global stage - with medicube.
        </div>
        <Description
          icon={
            <div className="bg-pink120 flex h-[48px] w-[48px] items-center justify-center rounded-full text-white">
              <Icons.SignIn.Product width={24} height={24} />
            </div>
          }
          title="Top Products, Sent Your Way"
          detail="Enjoy the latest launches and bestsellers from medicube and APRILSKIN."
        />
        <Description
          icon={
            <div className="bg-pink120 flex h-[48px] w-[48px] items-center justify-center rounded-full text-white">
              <Icons.SignIn.Reward width={24} height={24} />
            </div>
          }
          title="Celebrate Your Creativity With Real Rewards"
          detail="Earn meaningful compensation that reflects your creativity and dedication."
        />
        <Description
          icon={
            <div className="bg-pink120 flex h-[48px] w-[48px] items-center justify-center rounded-full text-white">
              <Icons.SignIn.AllInOne width={24} height={24} />
            </div>
          }
          title="All-in-One Creator App"
          detail="Submit, track, and earn — all in one experience."
        />
      </div>
    </PageContainer>
  );
}

function UnderReviewHeader() {
  return (
    <div className="text-gray90 fixed top-0 z-10 flex h-[48px] w-full max-w-[600px] items-center gap-[8px] bg-inherit px-[16px]">
      <div className="flex-1">
        <Icons.MediPro width={138} height={24} />
      </div>
      <div className="flex items-center gap-[12px]">
        <ProfileImage className="size-[24px] cursor-pointer" imagePath={undefined} />
      </div>
    </div>
  );
}

function Description({
  title,
  detail,
  icon,
  className,
  titleClassName,
}: {
  title: string;
  detail: string;
  icon: ReactNode;
  className?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn('flex gap-[12px]', className)}>
      <div className="shrink-0">{icon}</div>
      <div className="flex flex-col gap-[4px]">
        <div className={cn('typo-caption-1 font-semibold', titleClassName)}>{title}</div>
        <div className="typo-caption-2">{detail}</div>
      </div>
    </div>
  );
}

function HomeContent() {
  /**
   * TODO: 콘텐츠 상태 변경 팝업
   * */
  useEffect(() => {
    // openCampaignNotSelectedModal({ campaignId: 1 }); // 캠페인 선정 거절되었을 경우
    // openCampaignSelectedModal({ campaignId: 1 }); // 캠페인 선정 되었을 경우
    // openCampaignContentNotApprovedModal({ campaignId: 1 }); // 캠페인 콘텐츠 반려되었을 경우
    // openCampaignContentApprovedModal({ campaignId: 1 }); // 캠페인 콘텐츠 승인되었을 경우
    // openCampaignCodeApprovedModal({ campaignId: 1 }); // 광고 코드 승인되었을 경우
    // openCampaignCodeNotApprovedModal({ campaignId: 1 }); // 광고 코드 반려되었을 경우
  }, []);
  return (
    <div className={'mx-[16px]'}>
      <NewsfeedViewerCarousel />
    </div>
  );
}

function NewsfeedViewerCarousel() {
  const imageUrls = ['/image0.jpg', '/image1.jpg', '/image2.jpg'];

  return (
    <Card className={'border-none px-[16px] py-[20px] shadow-[0_0_4px_0_rgba(0,0,0,0.04)]'}>
      <div className={'typo-heading-1 text-pink120 font-bold'}>Newsfeed</div>
      <Spacing spacing={20} />
      <CustomCarousel
        className={'relative w-full min-w-full'}
        loop={false}
        pagination={CarouselPagination}
      >
        {imageUrls.map((imageUrl, index) => (
          <Img
            responsive
            key={imageUrl}
            className={'aspect-4/5 w-full rounded-[16px]'}
            alt={`image-viewer-${index}`}
            src={imageUrl}
            priority
          />
        ))}
      </CustomCarousel>
    </Card>
  );
}

function CarouselPagination(props: { current: number; count: number }) {
  const { current, count } = props;
  return (
    <>
      <Spacing spacing={20} />
      <div className={'flex justify-center gap-[8px]'}>
        {Array.from({ length: count }, (_, i) => (
          <div
            key={`carousel-pagination-${i}`}
            className={cn(
              'h-[8px] w-[8px] rounded-full',
              i === current - 1 ? 'bg-pink120' : 'bg-gray20',
            )}
          />
        ))}
      </div>
    </>
  );
}
